import type {
  AiData,
  AiEffort,
  AiModelSize,
  AiProviderName,
  EmailMode,
  NotificationChannel,
  PermissionScope,
  SettingValue,
  SmtpSecurity,
} from '@nexus/core';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * Answers of the administration endpoints of @nexus/core-server, and the queries the pages share.
 * Query keys start with `admin` so a company switch drops them together.
 */
export interface AccountRow {
  id: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'locked';
  lockReason: 'admin' | 'provider' | null;
  lastLoginAt: string | null;
  createdAt: string;
  version: number;
  /** Logs in through the SSO provider of the company. */
  sso: boolean;
  /** Login with email and password (ADR-0041). */
  login: { loginEmail: string | null; password: boolean; twoFactor: boolean; passkeys: number };
}

export interface AccountPage {
  items: AccountRow[];
  next: string | null;
}

export interface AccountDetail extends AccountRow {
  groups: { id: string; name: string; source: 'local' | 'provider' }[];
}

export interface AccountAssignment {
  id: string;
  roleId: string;
  roleName: string | null;
  roleTemplateName: string | null;
  groupId: string | null;
  groupName: string | null;
  everyone: boolean;
  validUntil: string | null;
}

export interface AccountAccess {
  assignments: AccountAssignment[];
  permissions: {
    permission: string;
    scope: PermissionScope | null;
    privileged: boolean;
    sources: { assignmentId: string; line: string }[];
  }[];
}

export interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  source: 'local' | 'provider';
  version: number;
  members: number;
}

export interface GroupMember {
  id: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'locked';
}

export interface RoleLine {
  permission: string;
  scope: PermissionScope | null;
}

export interface RoleRow {
  id: string;
  name: string | null;
  templateName: string | null;
  description: string | null;
  version: number;
  permissions: RoleLine[];
  assignments: number;
}

export interface CatalogEntry {
  key: string;
  owner: string;
  scopes: PermissionScope[];
  privileged: boolean;
  sensitive: boolean;
  i18nKey: string;
}

export interface RoleAssignment {
  id: string;
  accountId: string | null;
  accountName: string | null;
  groupId: string | null;
  groupName: string | null;
  everyone: boolean;
  validUntil: string | null;
  assignedBy: string | null;
  createdAt: string;
}

export interface ReportRow extends RoleAssignment {
  roleId: string;
  roleName: string | null;
  roleTemplateName: string | null;
  privileged: string[];
  selfAssigned: boolean;
}

export interface AuditEntry {
  id: string;
  companyId: string | null;
  occurredAt: string;
  recordedAt: string;
  actorKind: 'account' | 'system';
  actorAccountId: string | null;
  actorCompanyId: string | null;
  actorName: string | null;
  /** Only on entries of the company log. */
  actorErased?: boolean;
  superadmin?: boolean;
  selfChange?: boolean;
  offline?: boolean;
  action: string;
  targetType: string | null;
  targetId: string | null;
  outcome: 'success' | 'denied' | 'failure';
  details: Record<string, unknown>;
  requestId: string | null;
}

export interface AuditFilter {
  action?: string;
  outcome?: AuditEntry['outcome'];
  from?: string;
  to?: string;
}

export type SsoProtocol = 'oidc' | 'saml';
export type SamlAttribute = 'subject' | 'displayName' | 'email';

export interface SsoProvider {
  id: string;
  protocol: SsoProtocol;
  /** OIDC only. */
  issuer: string | null;
  clientId: string | null;
  scopes: string;
  status: 'draft' | 'active' | 'disabled';
  verifiedAt: string | null;
  hasSecret: boolean;
  /** Login with email and password next to SSO; always on without an active provider. */
  passwordLogin: boolean;
  redirectUri: string;
  /** SAML only: the identity provider from its metadata. */
  saml: {
    entityId: string;
    ssoUrl: string;
    certificates: { fingerprint: string; subject: string; notAfter: string }[];
    /** OIDC issuer of the Entra tenant, or `saml:<entity ID>` for accounts of their own. */
    accountIssuer: string;
    attributes: Record<SamlAttribute, string>;
  } | null;
  version: number;
}

/** What the SAML identity provider needs to know about the app. */
export interface SamlServiceProvider {
  entityId: string;
  acsUrl: string;
  metadata: string;
}

export interface NotificationSettings {
  emailMode: EmailMode;
  smtp: {
    host: string;
    port: number;
    security: SmtpSecurity;
    username: string | null;
    hasPassword: boolean;
    fromAddress: string;
    fromName: string;
  } | null;
  teams: {
    enabled: boolean;
    tenantId: string | null;
    clientId: string | null;
    hasClientSecret: boolean;
    appId: string | null;
  };
  push: { enabled: boolean };
  /** What the platform provides: its SMTP server. */
  platform: { smtp: boolean };
  version: number;
}

/** SharePoint connection of the company for the document store, without secrets. */
export interface SharePointSettings {
  enabled: boolean;
  tenantId: string | null;
  clientId: string | null;
  credential: 'certificate' | 'secret';
  hasClientSecret: boolean;
  hasPrivateKey: boolean;
  certificateThumbprint: string | null;
  site: string | null;
  library: string | null;
  rootFolder: string;
  siteName: string | null;
  verifiedAt: string | null;
  version: number;
}

export interface Branding {
  primaryColor: string | null;
  logo: string | null;
  version: number;
}

export interface CompanyRow {
  id: string;
  parentId: string | null;
  kind: 'company' | 'group';
  name: string;
  shortName: string | null;
  slug: string;
  status: 'active' | 'disabled';
  primaryColor: string | null;
  version: number;
}

export interface ModuleRow {
  id: string;
  name: string;
  icon: string;
  parent: string | null;
  scope: 'company' | 'group';
  dependsOn: string[];
  activations: { companyId: string | null; enabled: boolean }[];
}

export interface PlatformGrant {
  id: string;
  accountId: string;
  accountName: string | null;
  companyId: string;
  companyName: string | null;
  permission: string;
  grantedBy: string | null;
  grantedByName: string | null;
  createdAt: string;
}

export interface LinkSide {
  accountId: string;
  accountName: string | null;
  companyId: string;
  companyName: string | null;
}

export interface AccountLink {
  id: string;
  a: LinkSide;
  b: LinkSide;
  createdAt: string;
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

export const accountsQuery = (filter: { search?: string; status?: AccountRow['status'] }) =>
  infiniteQueryOptions({
    queryKey: ['admin', 'accounts', filter],
    queryFn: ({ pageParam, signal }) =>
      api.get<AccountPage>(`/accounts${query({ ...filter, cursor: pageParam })}`, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next ?? undefined,
  });

/** First matches of a name search, for pickers. */
export const accountSearchQuery = (search: string) =>
  queryOptions({
    queryKey: ['admin', 'accounts', 'search', search],
    queryFn: ({ signal }) =>
      api.get<AccountPage>(`/accounts${query({ search, status: 'active', limit: '20' })}`, {
        signal,
      }),
    placeholderData: (previous) => previous,
  });

export const accountQuery = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'accounts', id],
    queryFn: ({ signal }) => api.get<AccountDetail>(`/accounts/${id}`, { signal }),
  });

export const accountAccessQuery = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'accounts', id, 'access'],
    queryFn: ({ signal }) => api.get<AccountAccess>(`/accounts/${id}/access`, { signal }),
  });

export const groupsQuery = queryOptions({
  queryKey: ['admin', 'groups'],
  queryFn: ({ signal }) => api.get<GroupRow[]>('/groups', { signal }),
});

export const groupMembersQuery = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'groups', id, 'members'],
    queryFn: ({ signal }) => api.get<GroupMember[]>(`/groups/${id}/members`, { signal }),
  });

export const rolesQuery = queryOptions({
  queryKey: ['admin', 'roles'],
  queryFn: ({ signal }) => api.get<RoleRow[]>('/roles', { signal }),
});

export const permissionCatalogQuery = queryOptions({
  queryKey: ['admin', 'roles', 'catalog'],
  queryFn: ({ signal }) => api.get<CatalogEntry[]>('/roles/catalog', { signal }),
  staleTime: 5 * 60_000,
});

export const roleAssignmentsQuery = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'roles', id, 'assignments'],
    queryFn: ({ signal }) => api.get<RoleAssignment[]>(`/roles/${id}/assignments`, { signal }),
  });

export const roleReportQuery = queryOptions({
  queryKey: ['admin', 'roles', 'report'],
  queryFn: ({ signal }) => api.get<ReportRow[]>('/roles/report', { signal }),
});

export const adminSettingsQuery = queryOptions({
  queryKey: ['admin', 'settings'],
  queryFn: ({ signal }) =>
    api.get<Partial<Record<string, SettingValue>>>('/settings/admin', { signal }),
});

const AUDIT_PAGE = 50;

export const auditQuery = (area: 'company' | 'platform', filter: AuditFilter) =>
  infiniteQueryOptions({
    queryKey: ['admin', 'audit', area, filter],
    queryFn: ({ pageParam, signal }) =>
      api.get<AuditEntry[]>(
        `${area === 'platform' ? '/platform/audit' : '/audit'}${query({
          ...filter,
          before: pageParam,
          limit: String(AUDIT_PAGE),
        })}`,
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => (page.length === AUDIT_PAGE ? page.at(-1)?.id : undefined),
  });

export const ssoQuery = queryOptions({
  queryKey: ['admin', 'sso'],
  queryFn: ({ signal }) => api.get<SsoProvider | null>('/company/sso', { signal }),
});

export const samlServiceProviderQuery = queryOptions({
  queryKey: ['admin', 'sso', 'service-provider'],
  queryFn: ({ signal }) =>
    api.get<SamlServiceProvider>('/company/sso/service-provider', { signal }),
  staleTime: Infinity,
});

export const notificationSettingsQuery = queryOptions({
  queryKey: ['admin', 'notifications'],
  queryFn: ({ signal }) => api.get<NotificationSettings>('/company/notifications', { signal }),
});

export const sharePointSettingsQuery = queryOptions({
  queryKey: ['admin', 'integrations', 'sharepoint'],
  queryFn: ({ signal }) =>
    api.get<SharePointSettings>('/company/integrations/sharepoint', { signal }),
});

export const brandingQuery = queryOptions({
  queryKey: ['admin', 'branding'],
  queryFn: ({ signal }) => api.get<Branding>('/company/branding', { signal }),
});

export const companiesQuery = queryOptions({
  queryKey: ['admin', 'platform', 'companies'],
  queryFn: ({ signal }) => api.get<CompanyRow[]>('/platform/companies', { signal }),
});

export const modulesQuery = queryOptions({
  queryKey: ['admin', 'platform', 'modules'],
  queryFn: ({ signal }) => api.get<ModuleRow[]>('/platform/modules', { signal }),
});

/** A module as the own company sees it: the default of the platform and its own choice. */
export interface CompanyModuleRow {
  id: string;
  name: string;
  icon: string;
  parent: string | null;
  dependsOn: string[];
  standard: boolean;
  choice: boolean | null;
  active: boolean;
}

export const companyModulesQuery = queryOptions({
  queryKey: ['admin', 'company-modules'],
  queryFn: ({ signal }) => api.get<CompanyModuleRow[]>('/company/modules', { signal }),
});

export const grantsQuery = queryOptions({
  queryKey: ['admin', 'platform', 'grants'],
  queryFn: ({ signal }) => api.get<PlatformGrant[]>('/platform/grants', { signal }),
});

export const linksQuery = queryOptions({
  queryKey: ['admin', 'platform', 'links'],
  queryFn: ({ signal }) => api.get<AccountLink[]>('/platform/account-links', { signal }),
});

export const companyAccountsQuery = (companyId: string) =>
  queryOptions({
    queryKey: ['admin', 'platform', 'companies', companyId, 'accounts'],
    queryFn: ({ signal }) =>
      api.get<{ id: string; displayName: string }[]>(`/platform/companies/${companyId}/accounts`, {
        signal,
      }),
  });

/** How a retention period applies: a policy of the company, always, or with the person. */
export type RetentionMode = 'policy' | 'automatic' | 'erasure';

export interface RetentionItem {
  entity: string;
  /** Module ID or `core`. */
  owner: string;
  categories: string[];
  specialCategories: string[];
  proposed: { period: string; after: string };
  erase: 'anonymize' | 'delete';
  mode: RetentionMode;
  minimum: string | null;
  policy: {
    enabled: boolean;
    period: string;
    lastRunAt: string | null;
    lastCount: number | null;
    version: number;
  } | null;
}

export const retentionQuery = queryOptions({
  queryKey: ['admin', 'privacy', 'retention'],
  queryFn: ({ signal }) => api.get<RetentionItem[]>('/privacy/retention', { signal }),
});

export interface AiSettings {
  enabled: boolean;
  provider: AiProviderName;
  model: string | null;
  smallModel: string | null;
  /** `null`: the function decides, else the platform default. */
  effortStandard: AiEffort | null;
  effortSmall: AiEffort | null;
  hasApiKey: boolean;
  maxData: AiData;
  monthlyTokenLimit: number | null;
  disabledFunctions: string[];
  version: number;
  /** A key and a model exist for the provider, from the company or the platform. */
  ready: boolean;
  effectiveModel: string | null;
  effectiveSmallModel: string | null;
  platform: {
    providers: { provider: AiProviderName; model: string; smallModel: string }[];
    defaultEffort: Record<AiModelSize, AiEffort>;
  };
  functions: {
    key: string;
    owner: string;
    label: string;
    data: AiData;
    permission: string;
    model: AiModelSize;
    /** Reads the files of its records (ADR-0053). */
    files: boolean;
  }[];
  usage: {
    month: {
      calls: number;
      inputTokens: number;
      outputTokens: number;
      cacheReadTokens: number;
      cacheWriteTokens: number;
      /** Weighted as they count against the monthly limit. */
      tokens: number;
    };
    byFunction: { function: string; calls: number; failures: number; tokens: number }[];
  };
}

export const aiSettingsQuery = queryOptions({
  queryKey: ['admin', 'ai'],
  queryFn: ({ signal }) => api.get<AiSettings>('/ai/settings', { signal }),
});

export interface JobCounts {
  type: string;
  due: number;
  running: number;
  scheduled: number;
  failed24h: number;
}

/** Answer of `GET /api/platform/operations`: counts only, no personal data. */
export interface Operations {
  build: { version: string; commit: string | null };
  startedAt: string;
  uptimeSeconds: number;
  database: { latencyMs: number; pool: { total: number; idle: number; waiting: number } };
  migrations: { applied: number; pending: string[] };
  jobs: JobCounts[];
  events: { due: number; scheduled: number; failed24h: number; oldestDueAt: string | null };
  notificationFailures: { channel: NotificationChannel; count: number }[];
  unexpectedErrors: number;
  ai: { calls: number; failures: number; tokens: number };
}

export const operationsQuery = queryOptions({
  queryKey: ['admin', 'operations'],
  queryFn: ({ signal }) => api.get<Operations>('/platform/operations', { signal }),
  refetchInterval: 30_000,
});

export interface BackupFile {
  name: string;
  sizeBytes: number;
  createdAt: string;
}

/** Answer of `GET /api/platform/operations/backups`, from the status file of the host. */
export type Backups =
  | { available: false }
  | {
      available: true;
      running: boolean;
      requested: boolean;
      last: {
        startedAt: string;
        finishedAt: string | null;
        result: 'success' | 'failure' | 'running';
        name: string | null;
        sizeBytes: number | null;
      } | null;
      backups: BackupFile[];
      updatedAt: string | null;
    };

/** Every 5 seconds while a backup runs or waits, otherwise with the rest of the page. */
export const backupsQuery = queryOptions({
  queryKey: ['admin', 'operations', 'backups'],
  queryFn: ({ signal }) => api.get<Backups>('/platform/operations/backups', { signal }),
  refetchInterval: (query) => {
    const data = query.state.data;
    return data?.available && (data.running || data.requested) ? 5_000 : 30_000;
  },
});

export const requestBackup = () =>
  api.post<{ requestedAt: string }>('/platform/operations/backups');
